import { useState } from "react";

// APIs
import { getSalesLeadsCustomers } from "@/DataPages/api/crm/queries";

// Hooks
import { usePaginatedResource } from "@/Common/hooks/Pagination";
import { buildGetNextPageParam } from "@/Common/hooks/Pagination/helpers";

// Context
import { useCRMContext } from "@/DataPages/context/crm/CRMContext";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// MUI
import { Grid, Skeleton, Stack, Typography } from "@mui/material";

// Layouts
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";

// Components
import CustomersTable from "./CustomersTable";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// Helpers
import { and } from "@/Common/helpers/sonar";

// Types
import { TSelectOption } from "@/DataPages/types/crm/customers";
import { IPageParam } from "@/Common/api/types/pagination";
import { TSelectValue } from "@/Layouts/components/InputComponents/SmartSelector/interfaces";

// Images
import emptyTable from "@/assets/images/commons/empty_table.png";

// Constants
import { CRM_KEYS } from "../constants";

type TCustomersProps = {
	isActiveTab: boolean;
};

const PAGINATION_SIZE = 1000;
const INITIAL_PAGE_PARAM: IPageParam<string> = { after: "", first: PAGINATION_SIZE };
const EMPTY_RESULT = { edges: [], pageInfo: { hasNextPage: false, endCursor: "" } };

const options: TSelectOption[] = [
	{
		label: "Current Month",
		value: "currentMonth",
	},
	{
		label: "Last 3 months",
		value: "last3Months",
	},
	{
		label: "Last 6 Months",
		value: "last6Months",
	},
	{
		label: "Last 12 Months",
		value: "last12Months",
	},
	{ label: "All Time", value: "all" },
];

function ContentTitle(props: Readonly<{ title: string; isLoading?: boolean }>) {
	const { title } = props;
	return <Typography className={"page-title"}>{title}</Typography>;
}

export default function Customers(props: Readonly<TCustomersProps>) {
	const { isActiveTab } = props;
	const { locationId = "" } = useCRMContext();
	const [selectedOption, setSelectedOption] = useState<TSelectOption>(options[0]);

	const { data, isLoading, refetch, hasNextPage, isFetching } = usePaginatedResource({
		queryKey: [CRM_KEYS.QUERY.CUSTOMER, locationId, selectedOption],
		queryFn: async (params) => {
			const pageParam = params.pageParam as IPageParam<string> | undefined;
			const pagination = pageParam && typeof pageParam === "object" ? pageParam : INITIAL_PAGE_PARAM;
			if (!("after" in pagination) || !("first" in pagination)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}

			if (!locationId) {
				return EMPTY_RESULT;
			}

			const period = selectedOption.value;

			const paginatedResponse = await getSalesLeadsCustomers(locationId, period, pagination);

			if (!paginatedResponse) {
				return EMPTY_RESULT;
			}

			return paginatedResponse;
		},
		initialPageParam: INITIAL_PAGE_PARAM,
		getNextPageParam: buildGetNextPageParam(PAGINATION_SIZE),
		enabled: and(isActiveTab, !!locationId),
		fetchAllPages: true,
		staleTime: Infinity,
		gcTime: Infinity,
	});
	const { getSubscription } = useSubscriptions();
	const customersSubscription = getSubscription([CRM_KEYS.SUBSCRIPTION.CUSTOMER, locationId, selectedOption]);
	customersSubscription?.setInvalidate([CRM_KEYS.SUBSCRIPTION.CUSTOMER, locationId, selectedOption]);
	customersSubscription?.subscriptions?.onUpdateCustomers?.setCallback(() => refetch());

	const handleSelectChange = (selectedValue: TSelectValue) => {
		if (!selectedValue) {
			return;
		}
		setSelectedOption(selectedValue as TSelectOption);
	};

	const dataToShow = Boolean(data?.length);

	if (isLoading) {
		return (
			<Grid item xs={12} mt={1}>
				<Skeleton className="relative top-[0px]" variant="text" width={500} height={30} />
				<TableLoader toolbar buttons={3} content rows={5} multiTitles={5} />
			</Grid>
		);
	}

	return (
		<Grid container alignContent="flex-start" className="h-full">
			<Grid container item>
				<Grid item xs={12} height={512}>
					<SmartSelector
						classes="!w-96"
						selectBy="value"
						showBy={"label"}
						options={options as object[]}
						selected={selectedOption}
						inputProps={{
							placeholder: "Select Period",
							sx: { width: "200px" },
						}}
						listBoxProps={{
							sx: { maxHeight: "320px" },
						}}
						disabled={isFetching}
						onChangeAction={handleSelectChange}
						error={!Boolean(selectedOption)}
					/>
					{!dataToShow ? (
						<EmptyBody image={emptyTable} title={`No Customers to show`} bodyHeight={"435px"} className="!shadow-none" />
					) : (
						<Stack className="h-full mt-4">
							<ContentTitle title={`All Time Customers`} />
							<CustomersTable
								data={data}
								hasNextPage={hasNextPage}
								isFetching={isFetching}
								selectionOptions={options}
								selectedOption={selectedOption}
							/>
						</Stack>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
}
